import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Info,
  Globe,
  Phone,
  MapPin,
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  Loader2,
  AlertCircle,
  Search,
} from "lucide-react";
import {
  GetAxios,
  PostAxios,
  PatchAxios,
  DeleteAxios,
} from "../../helpers/axiosHelpers";
import { toast } from "sonner";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";

const informationSchema = Yup.object().shape({
  key: Yup.string()
    .required("Key is required")
    .matches(/^[a-z0-9_]+$/, "Key must be lowercase letters, numbers, and underscores only")
    .min(3, "Key must be at least 3 characters")
    .max(50, "Key must be at most 50 characters"),
  label: Yup.string().required("Label is required").min(2).max(100),
  value: Yup.string().required("Value is required").min(1).max(1000),
  category: Yup.string().required("Category is required").oneOf(["contact", "social", "location", "other"]),
  isPublic: Yup.string().required("Visibility is required").oneOf(["true", "false"]),
});

const CATEGORY_META = {
  contact: { icon: Phone, label: "Contact", color: "text-green-500", bg: "bg-green-50 border-green-100" },
  social: { icon: Globe, label: "Social Media", color: "text-blue-500", bg: "bg-blue-50 border-blue-100" },
  location: { icon: MapPin, label: "Location", color: "text-red-500", bg: "bg-red-50 border-red-100" },
  other: { icon: Info, label: "Other", color: "text-slate-500", bg: "bg-slate-50 border-slate-100" },
};

const InformationPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const formRef = useRef(null);

  const initialValues = { key: "", label: "", value: "", category: "contact", isPublic: "true" };

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await GetAxios("/information");
      if (response.success) setItems(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      toast.error("Failed to load information");
    } finally {
      setLoading(false);
    }
  };

  // Group and filter items
  const filteredItems = useMemo(() => {
    let result = items;
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      result = result.filter(
        (i) =>
          i.label.toLowerCase().includes(q) ||
          i.key.toLowerCase().includes(q) ||
          i.value.toLowerCase().includes(q)
      );
    }
    if (activeCategory !== "all") {
      result = result.filter((i) => i.category === activeCategory);
    }
    return result;
  }, [items, searchTerm, activeCategory]);

  const groupedItems = useMemo(() => {
    const groups = {};
    filteredItems.forEach((item) => {
      const cat = item.category || "other";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(item);
    });
    return groups;
  }, [filteredItems]);

  const categoryCounts = useMemo(() => {
    const counts = { all: items.length };
    items.forEach((i) => {
      counts[i.category] = (counts[i.category] || 0) + 1;
    });
    return counts;
  }, [items]);

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    try {
      const dataToSend = editingItem
        ? { label: values.label.trim(), value: values.value.trim(), category: values.category, isPublic: values.isPublic === "true" }
        : { key: values.key.trim().toLowerCase().replace(/\s+/g, "_"), label: values.label.trim(), value: values.value.trim(), category: values.category, isPublic: values.isPublic === "true" };

      const response = editingItem
        ? await PatchAxios(`/information/${editingItem.id}`, dataToSend)
        : await PostAxios("/information", dataToSend);

      if (response.success) {
        toast.success(editingItem ? "Updated successfully" : "Created successfully");
        await fetchItems();
        closeForm();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.msg || "Operation failed";
      toast.error(errorMsg);
      setErrors({ submit: errorMsg });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowForm(true);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.label}"?`)) return;
    try {
      const response = await DeleteAxios(`/information/${item.id}`);
      if (response.success) {
        toast.success("Deleted successfully");
        await fetchItems();
      }
    } catch (err) {
      toast.error(err.response?.data?.msg || "Failed to delete");
    }
  };

  const closeForm = () => { setShowForm(false); setEditingItem(null); };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-subidha-blue animate-spin mx-auto mb-4" />
          <p className="text-sm font-bold text-slate-400 uppercase tracking-wider">Loading Information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Search */}
      <div className="bg-white rounded-[2.5rem] shadow-xl border border-slate-100 p-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-black text-slate-900">Company Information</h1>
            <p className="text-sm text-slate-500 mt-1">
              {items.length} entries across {Object.keys(categoryCounts).length - 1} categories
            </p>
          </div>
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="px-6 py-3 bg-subidha-blue text-white rounded-xl font-bold text-sm hover:bg-slate-800 transition-all flex items-center space-x-2 shrink-0"
            >
              <Plus size={18} />
              <span>Add Information</span>
            </button>
          )}
        </div>

        {/* Search + Category Filter */}
        <div className="mt-6 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by label, key, or value..."
              className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold outline-none focus:ring-2 focus:ring-subidha-blue/20 focus:border-subidha-blue transition-all"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            {["all", "contact", "social", "location", "other"].map((cat) => {
              const isActive = activeCategory === cat;
              const meta = CATEGORY_META[cat];
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center gap-1.5 ${
                    isActive
                      ? "bg-subidha-blue text-white shadow-lg shadow-blue-500/20"
                      : "bg-slate-50 text-slate-500 hover:bg-slate-100 border border-slate-200"
                  }`}
                >
                  {cat === "all" ? "All" : meta?.label || cat}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isActive ? "bg-white/20" : "bg-slate-200"}`}>
                    {categoryCounts[cat] || 0}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <div ref={formRef} className="bg-white rounded-[2.5rem] shadow-xl border border-slate-100 p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-black text-slate-900">
              {editingItem ? "Edit Information" : "New Information"}
            </h2>
            <button onClick={closeForm} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all">
              <X size={20} />
            </button>
          </div>

          <Formik
            initialValues={
              editingItem
                ? { key: editingItem.key, label: editingItem.label, value: editingItem.value, category: editingItem.category, isPublic: editingItem.isPublic ? "true" : "false" }
                : initialValues
            }
            validationSchema={informationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ errors, touched, isSubmitting }) => (
              <Form className="space-y-5">
                {errors.submit && (
                  <div className="bg-red-50 border border-red-200 p-4 rounded-xl flex items-center space-x-3 text-red-600 text-sm font-bold">
                    <AlertCircle size={20} />
                    <span>{errors.submit}</span>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {!editingItem ? (
                    <div>
                      <label className="text-xs font-black text-slate-400 uppercase tracking-wider mb-2 block">Key (Unique ID) *</label>
                      <Field name="key" type="text" placeholder="e.g., company_email"
                        className={`w-full bg-white border ${errors.key && touched.key ? "border-red-500" : "border-slate-200"} px-4 py-3 rounded-xl font-bold text-sm outline-none focus:ring-2 focus:ring-subidha-blue/20 focus:border-subidha-blue transition-all`}
                      />
                      <ErrorMessage name="key" component="div" className="text-red-500 text-xs font-bold mt-1" />
                    </div>
                  ) : (
                    <div>
                      <label className="text-xs font-black text-slate-400 uppercase tracking-wider mb-2 block">Key (Unique ID)</label>
                      <Field name="key" type="text" readOnly disabled className="w-full bg-slate-50 border border-slate-200 px-4 py-3 rounded-xl font-bold text-sm text-slate-500 cursor-not-allowed" />
                      <p className="text-xs text-slate-400 mt-1 font-medium">Key cannot be changed after creation</p>
                    </div>
                  )}

                  <div className={!editingItem ? "" : "md:col-span-2"}>
                    <label className="text-xs font-black text-slate-400 uppercase tracking-wider mb-2 block">Display Label *</label>
                    <Field name="label" type="text" placeholder="e.g., Company Email"
                      className={`w-full bg-white border ${errors.label && touched.label ? "border-red-500" : "border-slate-200"} px-4 py-3 rounded-xl font-bold text-sm outline-none focus:ring-2 focus:ring-subidha-blue/20 focus:border-subidha-blue transition-all`}
                    />
                    <ErrorMessage name="label" component="div" className="text-red-500 text-xs font-bold mt-1" />
                  </div>

                  <div>
                    <label className="text-xs font-black text-slate-400 uppercase tracking-wider mb-2 block">Category *</label>
                    <Field as="select" name="category"
                      className={`w-full bg-white border ${errors.category && touched.category ? "border-red-500" : "border-slate-200"} px-4 py-3 rounded-xl font-bold text-sm outline-none focus:ring-2 focus:ring-subidha-blue/20 focus:border-subidha-blue transition-all`}
                    >
                      <option value="contact">Contact</option>
                      <option value="social">Social Media</option>
                      <option value="location">Location</option>
                      <option value="other">Other</option>
                    </Field>
                    <ErrorMessage name="category" component="div" className="text-red-500 text-xs font-bold mt-1" />
                  </div>

                  <div>
                    <label className="text-xs font-black text-slate-400 uppercase tracking-wider mb-2 block">Visibility *</label>
                    <Field as="select" name="isPublic"
                      className={`w-full bg-white border ${errors.isPublic && touched.isPublic ? "border-red-500" : "border-slate-200"} px-4 py-3 rounded-xl font-bold text-sm outline-none focus:ring-2 focus:ring-subidha-blue/20 focus:border-subidha-blue transition-all`}
                    >
                      <option value="true">Public (Visible on website)</option>
                      <option value="false">Private (Internal only)</option>
                    </Field>
                    <ErrorMessage name="isPublic" component="div" className="text-red-500 text-xs font-bold mt-1" />
                  </div>

                  <div className="md:col-span-2">
                    <label className="text-xs font-black text-slate-400 uppercase tracking-wider mb-2 block">Value *</label>
                    <Field as="textarea" name="value" placeholder="Enter the value..." rows={3}
                      className={`w-full bg-white border ${errors.value && touched.value ? "border-red-500" : "border-slate-200"} px-4 py-3 rounded-xl font-bold text-sm outline-none focus:ring-2 focus:ring-subidha-blue/20 focus:border-subidha-blue transition-all resize-none`}
                    />
                    <ErrorMessage name="value" component="div" className="text-red-500 text-xs font-bold mt-1" />
                  </div>
                </div>

                <div className="flex space-x-3">
                  <button type="button" onClick={closeForm} className="flex-1 px-6 py-3 rounded-xl font-black text-slate-500 bg-white border border-slate-200 hover:bg-slate-100 transition-all uppercase text-xs tracking-wider">
                    Cancel
                  </button>
                  <button type="submit" disabled={isSubmitting} className="flex-1 px-6 py-3 rounded-xl font-black text-white bg-subidha-blue hover:bg-slate-800 transition-all disabled:opacity-50 uppercase text-xs tracking-wider flex items-center justify-center space-x-2">
                    {isSubmitting ? <Loader2 className="animate-spin" size={18} /> : (<><Save size={18} /><span>{editingItem ? "Update" : "Create"}</span></>)}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      )}

      {/* Grouped Items */}
      {filteredItems.length > 0 ? (
        Object.entries(groupedItems)
          .sort(([a], [b]) => {
            const order = ["contact", "social", "location", "other"];
            return order.indexOf(a) - order.indexOf(b);
          })
          .map(([category, catItems]) => {
            const meta = CATEGORY_META[category] || CATEGORY_META.other;
            const CatIcon = meta.icon;
            return (
              <div key={category}>
                {/* Category Header */}
                <div className="flex items-center gap-3 mb-4 px-1">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${meta.bg} border`}>
                    <CatIcon size={16} className={meta.color} />
                  </div>
                  <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider">
                    {meta.label}
                  </h3>
                  <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                    {catItems.length}
                  </span>
                  <div className="flex-1 h-px bg-slate-100" />
                </div>

                {/* Items Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                  {catItems.map((item) => (
                    <div
                      key={item.id}
                      className="bg-white rounded-2xl shadow-md border border-slate-100 p-5 hover:shadow-xl transition-all group"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-black text-slate-900 truncate">
                            {item.label}
                          </h3>
                          {item.isPublic ? (
                            <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-green-50 text-green-600 uppercase shrink-0">
                              Public
                            </span>
                          ) : (
                            <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 uppercase shrink-0">
                              Private
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                          <button onClick={() => handleEdit(item)} className="p-1.5 text-slate-400 hover:text-subidha-blue hover:bg-blue-50 rounded-lg transition-all">
                            <Edit2 size={14} />
                          </button>
                          <button onClick={() => handleDelete(item)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                      <p className="text-[11px] text-slate-400 font-mono mb-1.5">{item.key}</p>
                      <p className="text-sm text-slate-700 font-medium break-words line-clamp-2">
                        {item.value}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
      ) : (
        <div className="text-center py-20">
          {searchTerm || activeCategory !== "all" ? (
            <div>
              <Search size={48} className="text-slate-200 mx-auto mb-4" />
              <p className="text-slate-400 font-bold uppercase tracking-wider text-sm">
                No results found
              </p>
              <button
                onClick={() => { setSearchTerm(""); setActiveCategory("all"); }}
                className="mt-4 px-6 py-2 bg-slate-100 text-slate-600 rounded-xl font-bold text-sm hover:bg-slate-200 transition-all"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <div>
              <Info size={48} className="text-slate-300 mx-auto mb-4" />
              <p className="text-slate-400 font-bold uppercase tracking-wider text-sm">No information entries yet</p>
              <button onClick={() => setShowForm(true)} className="mt-4 px-6 py-2 bg-subidha-blue text-white rounded-xl font-bold text-sm hover:bg-slate-800 transition-all">
                Add First Entry
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InformationPage;
